import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import { VideoItem } from "../types/video.js";

export interface PlayerContextType {
  activeVideo: VideoItem | null;
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  isMiniplayerVisible: boolean;
  seekTarget: number | null;
  playVideo: (video: VideoItem, startPlaying?: boolean) => void;
  pauseVideo: () => void;
  resumeVideo: () => void;
  togglePlay: () => void;
  updateTime: (current: number, dur?: number) => void;
  seekTo: (seconds: number) => void;
  closeMiniplayer: () => void;
  expandMiniplayer: () => void;
  setMiniplayerVisible: (visible: boolean) => void;
  onExpandRef: React.MutableRefObject<((video: VideoItem) => void) | null>;
}

const PlayerContext = createContext<PlayerContextType | undefined>(undefined);

export const PlayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isMiniplayerVisible, setIsMiniplayerVisible] = useState<boolean>(false);
  const [seekTarget, setSeekTarget] = useState<number | null>(null);

  const onExpandRef = useRef<((video: VideoItem) => void) | null>(null);

  const playVideo = useCallback((video: VideoItem, startPlaying = true) => {
    setActiveVideo(video);
    setIsPlaying(startPlaying);
    setIsMiniplayerVisible(false);
  }, []);

  const pauseVideo = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const resumeVideo = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const updateTime = useCallback((current: number, dur?: number) => {
    setCurrentTime(current);
    if (typeof dur === "number" && dur > 0) {
      setDuration(dur);
    }
  }, []);

  const seekTo = useCallback((seconds: number) => {
    setSeekTarget(seconds);
    setCurrentTime(seconds);
    setTimeout(() => setSeekTarget(null), 100);
  }, []);

  const closeMiniplayer = useCallback(() => {
    setIsMiniplayerVisible(false);
    setIsPlaying(false);
  }, []);

  const expandMiniplayer = useCallback(() => {
    setIsMiniplayerVisible(false);
    if (activeVideo && onExpandRef.current) {
      onExpandRef.current(activeVideo);
    }
  }, [activeVideo]);

  const setMiniplayerVisible = useCallback((visible: boolean) => {
    setIsMiniplayerVisible(visible);
  }, []);

  return (
    <PlayerContext.Provider
      value={{
        activeVideo,
        isPlaying,
        currentTime,
        duration,
        isMiniplayerVisible,
        seekTarget,
        playVideo,
        pauseVideo,
        resumeVideo,
        togglePlay,
        updateTime,
        seekTo,
        closeMiniplayer,
        expandMiniplayer,
        setMiniplayerVisible,
        onExpandRef,
      }}
    >
      {children}
    </PlayerContext.Provider>
  );
};

export const usePlayer = (): PlayerContextType => {
  const context = useContext(PlayerContext);
  if (!context) {
    throw new Error("usePlayer must be used within a PlayerProvider");
  }
  return context;
};

export const usePlayerSafe = (): PlayerContextType | null => {
  try {
    const dispatcher = (React as any)?.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED?.ReactCurrentDispatcher?.current;
    if (!dispatcher) return null;
    return useContext(PlayerContext) || null;
  } catch {
    return null;
  }
};
